import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDebouncedValue } from '@/hooks/useDebouncedValue.hook';
import { paymentsApi } from '@/services/payments.service';
import type {
  AdminPaymentRecord,
  PaymentListResult,
  PaymentsSummary,
  PaymentTab,
} from '@/types/payment.types';

import { PAGE_SIZE, PAYMENTS_QUERY_KEY } from '../constants';

/** Which inline confirm-form is open in the detail drawer. */
export type PaymentActionForm = 'reject' | 'settle' | 'refund' | null;

export interface UsePaymentsPageResult {
  records: AdminPaymentRecord[];
  total: number;
  page: number;
  totalPages: number;
  tab: PaymentTab;
  search: string;
  summary: PaymentsSummary | undefined;
  selected: AdminPaymentRecord | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  mutatingId: string | null;
  actionError: string | null;
  openForm: PaymentActionForm;
  draft: string;
  onChangeTab: (tab: PaymentTab) => void;
  onChangeSearch: (search: string) => void;
  onChangePage: (page: number) => void;
  onSelect: (record: AdminPaymentRecord) => void;
  onCloseDrawer: () => void;
  onOpenForm: (form: PaymentActionForm) => void;
  onDraftChange: (value: string) => void;
  onVerify: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onSettle: (id: string, adminRef: string) => void;
  onRefundDeposit: (id: string, ref?: string) => void;
}

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong';

export const usePaymentsPage = (): UsePaymentsPageResult => {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<PaymentTab>('submitted');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState<PaymentActionForm>(null);
  const [draft, setDraft] = useState('');

  const debouncedSearch = useDebouncedValue(search, 300);

  const listQuery = useQuery<PaymentListResult>({
    queryKey: [PAYMENTS_QUERY_KEY, 'list', { tab, search: debouncedSearch, page }],
    queryFn: () =>
      paymentsApi.list({
        tab,
        search: debouncedSearch || undefined,
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: (previous) => previous,
  });

  const summaryQuery = useQuery<PaymentsSummary>({
    queryKey: [PAYMENTS_QUERY_KEY, 'summary'],
    queryFn: () => paymentsApi.summary(),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
  };

  const runMutation = (
    id: string,
    fn: () => Promise<AdminPaymentRecord>,
  ): void => {
    setActionError(null);
    setMutatingId(id);
    fn()
      .catch((error: unknown) => setActionError(messageOf(error)))
      .finally(() => {
        setMutatingId(null);
        invalidate();
      });
  };

  const verifyMutation = useMutation({ mutationFn: (id: string) => paymentsApi.verify(id) });
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => paymentsApi.reject(id, reason),
  });
  const settleMutation = useMutation({
    mutationFn: ({ id, adminRef }: { id: string; adminRef: string }) =>
      paymentsApi.settle(id, adminRef),
  });
  const refundMutation = useMutation({
    mutationFn: ({ id, ref }: { id: string; ref?: string }) => paymentsApi.refundDeposit(id, ref),
  });

  const records = listQuery.data?.items ?? [];
  const selected = selectedId ? records.find((r) => r.id === selectedId) ?? null : null;

  const resetForm = (): void => {
    setOpenForm(null);
    setDraft('');
  };

  return {
    records,
    total: listQuery.data?.total ?? 0,
    page: listQuery.data?.page ?? page,
    totalPages: listQuery.data?.totalPages ?? 1,
    tab,
    search,
    summary: summaryQuery.data,
    selected,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    errorMessage: listQuery.error instanceof Error ? listQuery.error.message : null,
    mutatingId,
    actionError,
    onChangeTab: (next) => {
      setTab(next);
      setPage(1);
    },
    onChangeSearch: (next) => {
      setSearch(next);
      setPage(1);
    },
    onChangePage: setPage,
    openForm,
    draft,
    onSelect: (record) => {
      setActionError(null);
      resetForm();
      setSelectedId(record.id);
    },
    onCloseDrawer: () => {
      setSelectedId(null);
      resetForm();
    },
    onOpenForm: (form) => {
      setActionError(null);
      setDraft('');
      setOpenForm(form);
    },
    onDraftChange: setDraft,
    onVerify: (id) => runMutation(id, () => verifyMutation.mutateAsync(id)),
    onReject: (id, reason) => {
      resetForm();
      runMutation(id, () => rejectMutation.mutateAsync({ id, reason }));
    },
    onSettle: (id, adminRef) => {
      resetForm();
      runMutation(id, () => settleMutation.mutateAsync({ id, adminRef }));
    },
    onRefundDeposit: (id, ref) => {
      resetForm();
      runMutation(id, () => refundMutation.mutateAsync({ id, ref }));
    },
  };
};
